import React, { useEffect, useMemo, useRef, useState } from "react";
import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import "../../../assets/topstudio/styles/CardsSprite.css";
import "../../../assets/topstudio/styles/aviator.css";
import "./Aviator.scoped.css";
import useSocket from "../../../hooks/useSocket";
import axiosInstance from "../../../utils/axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AviatorRules from "./pop-up/aviator-rules";
import BgSound from "../../../assets/topstudio/audio/aviatorbg.mp3";
import FlewAwaySound from "../../../assets/topstudio/audio/crashedmusic.mp3";

import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import AvaitorResult from "../../client/tp-studio/tp-result/AvaitorResult.jsx";
import { Check, X } from "lucide-react";

/** Timing & flight settings */
const STEP = 0.01; // per tick
const STEP_MS = 91; // ms per tick after 1.10
const SLOW_UNTIL = 1.1;
const CASHOUT_MIN_MULTIPLIER = 1;
const SLOW_DURATION_MS = 2000; // 1.00 → 1.10
const SLOW_RANGE_STEPS = Math.round((SLOW_UNTIL - 1) / STEP);
const SLOW_STEP_MS = SLOW_DURATION_MS / SLOW_RANGE_STEPS;
const CLIMB_DURATION = 2000; // ms for plane to reach the top
const CRASHED_DISPLAY_MS = 3000; // how long to show "FLEW AWAY"
const CLOSED_RESET_MS = 2000; // reset counter 2s after crash/CLOSED
const SERVER_TICK_MS = 200; //added by uk - filler uses 200ms up to 1.10, then 91ms, same as Aviator
const LUCKY_PLAN_TARGET = 3.5; // dummy cashouts without knowing the crash

const getAxisDotSize = () => (window.innerWidth < 768 ? 2 : 3);
const getAxisDotHSpacing = () => (window.innerWidth < 768 ? 100 : 150);
const AXIS_DOT_V_SPACING = 80;
const AXIS_DOT_SCROLL_SPEED = 2;

const drawAvxRotatingBackground = (ctx, W, H, rotationAngleRef, animate = true) => {
  ctx.save();
  ctx.translate(0, H);
  ctx.rotate(rotationAngleRef.current);
  const numLines = 30;
  const lineLength = Math.hypot(W, H);
  const maxLineWidth = window.innerWidth < 768 ? 40 : 60;
  for (let i = 0; i < numLines; i++) {
    ctx.beginPath();
    const gradient = ctx.createLinearGradient(0, 0, lineLength, 0);
    const alpha = i % 2 === 0 ? 0.01 : 0.05;
    gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
    gradient.addColorStop(1, `rgba(255, 255, 255, ${alpha})`);
    ctx.strokeStyle = gradient;
    ctx.moveTo(0, 0);
    ctx.lineTo(lineLength, 0);
    ctx.lineWidth = maxLineWidth;
    ctx.stroke();
    ctx.rotate((Math.PI * 2) / numLines);
  }
  ctx.restore();
  if (animate) rotationAngleRef.current += 0.002;
};

const drawAvxCenteredGradient = (ctx, W, H) => {
  const centerX = W / 2;
  const centerY = H / 2;
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    W / 2
  );
  gradient.addColorStop(0, "rgba(128, 0, 128, 0.7)");
  gradient.addColorStop(0.3, "rgba(128, 0, 128, 0.3)");
  gradient.addColorStop(0.6, "rgba(128, 0, 128, 0.1)");
  gradient.addColorStop(1, "rgba(128, 0, 128, 0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, W, H);
};

const drawAvxDividerLines = (ctx, W, H) => {
  const bottomDividerY = H - 20;
  const leftDividerX = 20;
  ctx.beginPath();
  ctx.moveTo(0, bottomDividerY);
  ctx.lineTo(W, bottomDividerY);
  ctx.lineWidth = 1;
  ctx.strokeStyle = "#888";
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(leftDividerX, 0);
  ctx.lineTo(leftDividerX, H);
  ctx.strokeStyle = "#888";
  ctx.stroke();
};

const drawAvxMovingDots = (ctx, W, H, dotOffset) => {
  const dotSize = getAxisDotSize();
  const spacing = getAxisDotHSpacing();
  const bottomY = H - 10;
  ctx.beginPath();
  for (let x = W + dotOffset; x > -W; x -= spacing) {
    ctx.arc(x, bottomY, dotSize, 0, Math.PI * 2);
  }
  ctx.fillStyle = "white";
  ctx.fill();
};

const drawAvxVerticalDots = (ctx, W, H, verticalDotOffset) => {
  const dotSize = getAxisDotSize();
  const leftX = 10;
  ctx.beginPath();
  for (let y = -H + verticalDotOffset; y < H; y += AXIS_DOT_V_SPACING) {
    ctx.arc(leftX, y, dotSize, 0, Math.PI * 2);
  }
  ctx.fillStyle = "white";
  ctx.fill();
};

const advanceAxisDotOffsets = (dotOffset, verticalDotOffset) => {
  const hSpacing = getAxisDotHSpacing();
  let nextDot = dotOffset - AXIS_DOT_SCROLL_SPEED;
  if (nextDot <= -hSpacing) nextDot = 0;
  let nextVertical = verticalDotOffset + AXIS_DOT_SCROLL_SPEED;
  if (nextVertical >= AXIS_DOT_V_SPACING) nextVertical = 0;
  return { dotOffset: nextDot, verticalDotOffset: nextVertical };
};

const PLANE_TOP_Y_RATIO = 0.12;
const PLANE_SWAY_LOW_Y_RATIO = 0.35;
const PLANE_STOP_X_RATIO = 0.8;
const PLANE_SWAY_EASE_MS = 1200;
const PLANE_HOVER_SMOOTH_MS = 240;
const PLANE_HOVER_NOISE_SPEED = 0.00042;

const createPlaneHoverState = () => ({
  active: false,
  x: 0,
  y: 0
});

const resetPlaneHoverState = (state) => {
  state.active = false;
};

const smoothNoise1D = (t) => {
  const i = Math.floor(t);
  const f = t - i;
  const u = f * f * (3 - 2 * f);
  const hash = (n) => {
    const v = Math.sin(n * 127.1 + n * 311.7) * 43758.5453;
    return v - Math.floor(v);
  };
  return hash(i) * (1 - u) + hash(i + 1) * u;
};

const stepPlaneHover = (state, hoverElapsedMs, deltaMs, W, H) => {
  const stopX = W * PLANE_STOP_X_RATIO;
  const topY = H * PLANE_TOP_Y_RATIO;
  const lowY = H * PLANE_SWAY_LOW_Y_RATIO;
  const horizontalAmp = W * 0.065;
  const dt = Math.min(Math.max(deltaMs, 8), 32);

  if (!state.active) {
    state.active = true;
    state.x = stopX;
    state.y = topY;
  }

  const t = hoverElapsedMs * PLANE_HOVER_NOISE_SPEED;
  const idealX = stopX + (smoothNoise1D(t) * 2 - 1) * horizontalAmp;
  const idealY =
    topY + smoothNoise1D(t * 0.83 + 41.7) * (lowY - topY);

  const alpha = 1 - Math.exp(-dt / PLANE_HOVER_SMOOTH_MS);
  state.x += (idealX - state.x) * alpha;
  state.y += (idealY - state.y) * alpha;

  return { tipX: state.x, tipY: state.y, anchorX: stopX, anchorY: topY };
};

/** Climb to top, then drift smoothly within the hover zone */
const getPlaneFlightTip = (elapsed, climbProgress, W, H, hoverState, deltaMs) => {
  const startX = 0;
  const startY = H;
  const stopX = W * PLANE_STOP_X_RATIO;
  const topY = H * PLANE_TOP_Y_RATIO;
  const easedClimb = 1 - Math.pow(1 - Math.min(1, climbProgress), 3);

  let tipX = startX + (stopX - startX) * easedClimb;
  let tipY = startY - (startY - topY) * Math.pow(easedClimb, 2);

  if (climbProgress >= 1 && hoverState) {
    const timeSinceTop = Math.max(0, elapsed - CLIMB_DURATION);
    const easeFactor = Math.min(timeSinceTop / PLANE_SWAY_EASE_MS, 1);
    const hover = stepPlaneHover(
      hoverState,
      timeSinceTop,
      deltaMs,
      W,
      H
    );

    tipX += (hover.tipX - hover.anchorX) * easeFactor;
    tipY += (hover.tipY - hover.anchorY) * easeFactor;
  }

  return { tipX, tipY };
};

const drawAvxCrashedCanvas = (ctx, W, H, rotationAngleRef, dotOffset, verticalDotOffset) => {
  ctx.clearRect(0, 0, W, H);
  drawAvxRotatingBackground(ctx, W, H, rotationAngleRef, false);
  drawAvxMovingDots(ctx, W, H, dotOffset);
  drawAvxVerticalDots(ctx, W, H, verticalDotOffset);
  drawAvxDividerLines(ctx, W, H);
};

function formatWinToastAmount(amount) {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toFixed(2);
}

function showWinToast(amount) {
  const display = formatWinToastAmount(amount);
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You won {display}</span>
    </div>,
    {
      toastId: "avx-win-toast",
      className: "av-win-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center",
    }
  );
}

function showLostToast(amount) {
  const display = formatWinToastAmount(amount);
  toast.dismiss("avx-win-toast");
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <X size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You lost {display}</span>
    </div>,
    {
      className: "av-lost-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showBetPlacedToast() {
  //console.log("order place...");
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">Order Placed</span>
    </div>,
    {
      toastId: "avx-bet-placed-toast",
      className: "av-bet-toast",
      icon: false,
      closeButton: false,
      autoClose: 1000,
      hideProgressBar: false,
      position: "top-right"
    }
  );
}

const normalizeResultsList = (data) => {
  if (data == null) return [];
  const values = Array.isArray(data) ? data : Object.values(data);
  return values
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map((v) => parseFloat(v))
    .filter((v) => !Number.isNaN(v));
};

const LAUNCH_COUNTDOWN_DEFAULT = 4;
const PRE_FLIGHT_STREAM_WINDOW_MS = (LAUNCH_COUNTDOWN_DEFAULT + 2) * 1000;

const parseServerSeconds = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : null;
};

const RESULT_SYNC_BUFFER = 0.02;

const getTotalFlightSteps = (target) =>
  Math.max(1, Math.round((target - 1) / STEP) + 1);

const getFlightDurationSec = (target) =>
  (getTotalFlightSteps(target) - 1) * (STEP_MS / 1000);

/** Never exceed crash result; stay a hair below when joining near the end */
const clampSyncedFlightValue = (value, target) => {
  let v = Math.min(Math.max(1, value), target);
  const safeCeiling = +(target - RESULT_SYNC_BUFFER).toFixed(2);
  if (safeCeiling >= 1 && v > safeCeiling) {
    v = safeCeiling;
  }
  return +v.toFixed(2);
};

/** Where the flight clock should stand for a given multiplier (2s slow start, then 91ms ticks). */
const elapsedMsFromMultiplier = (value, skipSlowStart) => {
  const v = Math.max(1, Number(value) || 1);
  if (skipSlowStart) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * STEP_MS;
  }
  if (v <= SLOW_UNTIL + 0.0001) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * SLOW_STEP_MS;
  }
  const extraSteps = Math.max(0, Math.round((v - SLOW_UNTIL) / STEP));
  return SLOW_DURATION_MS + extraSteps * STEP_MS;
};

const multiplierToStep = (value, target) => {
  const totalSteps = getTotalFlightSteps(target);
  const step = Math.round((value - 1) / STEP) + 1;
  return Math.min(totalSteps, Math.max(1, step));
};

const MUSLIM_NAMES = [
  "Muhammad",
  "Ahmed",
  "Ali",
  "Hassan",
  "Hussein",
  "Omar",
  "Usman",
  "Bilal",
  "Hamza",
  "Yusuf",
  "Ibrahim",
  "Khalid",
  "Tariq",
  "Imran",
  "Farhan",
  "Saeed",
  "Rashid",
  "Nadeem",
  "Asif",
  "Faisal",
  "Zubair",
  "Salman",
  "Waqas",
  "Junaid",
  "Aamir",
  "Shahid",
  "Noman",
  "Rehan",
  "Sajid",
  "Kamran",
  "Arif",
  "Javed",
  "Fatima",
  "Aisha",
  "Zainab",
  "Maryam",
  "Khadija",
  "Hafsa",
  "Sana",
  "Hina",
  "Rabia",
  "Nadia",
  "Samina",
  "Shazia",
  "Farah",
  "Sadia",
  "Ayesha",
  "Bushra",
  "Uzma",
  "Nazia",
  "Saima",
  "Rubina",
  "Tahira",
  "Yasmin",
  "Amna",
  "Laiba",
  "Mahnoor",
  "Hira",
  "Kinza",
  "Aliza",
  "Danish",
  "Haroon",
  "Majid",
  "Rizwan"
];

const luckyRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const maskLuckyPlayerName = (name) => {
  if (!name) return "****";
  const visible = name.slice(0, Math.min(4, name.length));
  return `${visible}****`;
};

const formatLuckyPlayerAmount = (value) =>
  Number(value).toLocaleString("en-IN");

/** profit = (size × price) − size */
const calcLuckyPlayerProfit = (price, amountRaw) => {
  const priceNum = parseFloat(price);
  const amount = Number(amountRaw);
  if (!Number.isFinite(priceNum) || !Number.isFinite(amount) || amount <= 0) {
    return "";
  }
  const profit = priceNum * amount - amount;
  return formatLuckyPlayerAmount(Math.round(profit));
};

/** Mid-flight join — use server-computed running_result when available */
const getFlightSyncFromRunningResult = (target, runningResult) => {
  const raw = parseFloat(runningResult);
  if (!Number.isFinite(raw) || raw <= 0) return null;

  const value = clampSyncedFlightValue(Math.min(raw, target), target);
  const step = multiplierToStep(value, target);
  const finalValue = clampSyncedFlightValue(
    +(1 + (step - 1) * STEP).toFixed(2),
    target
  );
  const finalStep = multiplierToStep(finalValue, target);

  return {
    step: finalStep,
    value: finalValue,
    elapsedMs: (finalStep - 1) * STEP_MS
  };
};

const LUCKY_RESERVE_FRACTION = 0.12;
const LUCKY_MIN_RESERVE = 8;
const LUCKY_MAX_RESERVE = 25;
const LUCKY_MIN_STEP_GAP = 5;

const luckyBetIsOpen = (bet) =>
  !bet.locked && (bet.rate === "--" || bet.profit === "" || bet.profit == null);

const pickRandomOpenBetIds = (bets, count, reserveCount = 0) => {
  const open = bets.filter(luckyBetIsOpen);
  const maxPickable = Math.max(0, open.length - reserveCount);
  if (maxPickable <= 0) return new Set();
  const n = Math.min(count, maxPickable);
  const shuffled = [...open].sort(() => Math.random() - 0.5);
  return new Set(shuffled.slice(0, n).map((bet) => bet.id));
};

const applyRandomLuckyCashoutBatch = (
  bets,
  price,
  batchSize,
  reserveCount = 0
) => {
  const selectedIds = pickRandomOpenBetIds(bets, batchSize, reserveCount);
  if (!selectedIds.size) return bets;
  return bets.map((bet) =>
    selectedIds.has(bet.id) ? lockLuckyBetAtPrice(bet, price) : bet
  );
};

const applyLuckyCashoutEvents = (bets, events, reserveCount) =>
  events.reduce(
    (acc, event) =>
      applyRandomLuckyCashoutBatch(
        acc,
        event.value.toFixed(2),
        event.batchSize,
        reserveCount
      ),
    bets
  );

/** Spread cashouts evenly across the flight; leave a slice of bets blank until the end */
const buildLuckyCashoutPlan = (target, totalBets) => {
  const reserveCount = Math.min(
    totalBets - 1,
    Math.max(
      LUCKY_MIN_RESERVE,
      Math.min(LUCKY_MAX_RESERVE, Math.ceil(totalBets * LUCKY_RESERVE_FRACTION))
    )
  );
  const toCashOut = Math.max(0, totalBets - reserveCount);
  const totalSteps = getTotalFlightSteps(target);
  const startStep = Math.max(1, Math.floor(totalSteps * 0.08));
  const endStep = Math.max(startStep + 1, Math.floor(totalSteps * 0.9));
  const usableSteps = endStep - startStep;
  const maxWaves = Math.max(1, Math.floor(usableSteps / LUCKY_MIN_STEP_GAP));
  const waveCount = Math.min(toCashOut, maxWaves);

  if (waveCount <= 0 || toCashOut <= 0) {
    return { events: [], reserveCount, nextEventIndex: 0 };
  }

  const baseBatch = Math.floor(toCashOut / waveCount);
  let remainder = toCashOut % waveCount;
  const events = [];

  for (let i = 0; i < waveCount; i++) {
    const step =
      waveCount === 1
        ? Math.floor((startStep + endStep) / 2)
        : startStep + Math.round((i / (waveCount - 1)) * usableSteps);
    const value = clampSyncedFlightValue(1 + (step - 1) * STEP, target);
    let batchSize = baseBatch + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    if (batchSize > 0) events.push({ value, batchSize });
  }

  return { events, reserveCount, nextEventIndex: 0 };
};

const lockLuckyBetAtPrice = (bet, price) => {
  if (bet.locked) return bet;
  return {
    ...bet,
    rate: price,
    profit: calcLuckyPlayerProfit(price, bet.amountRaw),
    locked: true
  };
};

const createDummyLuckyBet = () => {
  const baseName = MUSLIM_NAMES[luckyRandomInt(0, MUSLIM_NAMES.length - 1)];
  const name = `${baseName}${luckyRandomInt(10, 99)}`;
  const amount = luckyRandomInt(3, 50) * 1000;

  return {
    id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    uname: maskLuckyPlayerName(name),
    amount: formatLuckyPlayerAmount(amount),
    amountRaw: amount,
    rate: "--",
    profit: ""
  };
};

const createDummyBetQueue = () => {
  const count = luckyRandomInt(100, 180);
  return Array.from({ length: count }, createDummyLuckyBet);
};

const parseLuckyPlayerEntry = (
  entry,
  { isUser = false, betId = null } = {}
) => {
  try {
    const obj = typeof entry === "string" ? JSON.parse(entry) : entry;
    const id = betId || obj._id || obj.bet_id;
    if (!id) return null;

    const name = obj.uname || "";
    const amountRaw = Number(obj.lockamt || obj.delivery || obj.amount || 0);
    const rateVal = obj.rate;
    const rateNum = parseFloat(rateVal);
    const hasCashout =
      rateVal != null &&
      rateVal !== "" &&
      rateVal !== "-" &&
      rateVal !== "--" &&
      Number.isFinite(rateNum) &&
      rateNum > 1;
    const rate = hasCashout ? Number(rateNum).toFixed(2) : "--";
    const profit =
      hasCashout && amountRaw ? calcLuckyPlayerProfit(rate, amountRaw) : "";

    return {
      id: String(id),
      betId: id,
      sectionNo: obj.section_no,
      uname: maskLuckyPlayerName(name),
      amount: formatLuckyPlayerAmount(amountRaw),
      amountRaw,
      rate,
      profit,
      locked: hasCashout,
      isUser
    };
  } catch {
    return null;
  }
};

const buildUserRecordsFromReturnBets = (returnBets, amounts, displayName) =>
  returnBets
    .filter((bet) => bet?.bet_id)
    .map((returnBet, idx) => {
      const betId = String(returnBet.bet_id);
      const amountRaw = Number(amounts[idx] || 0);
      return {
        id: betId,
        betId,
        sectionNo: returnBet.section_no,
        uname: maskLuckyPlayerName(displayName),
        amount: formatLuckyPlayerAmount(amountRaw),
        amountRaw,
        rate: "--",
        profit: "",
        locked: false,
        isUser: true
      };
    });

/**
 * Mid-flight reload sync — left_time is elapsed seconds since flight started.
 * Maps elapsed time onto the result-scaled flight window (not game_time).
 */
const getFlightSyncFromLeftTime = (target, leftTime) => {
  const elapsed = parseServerSeconds(leftTime);
  if (elapsed == null) return null;

  const flightDurationSec = getFlightDurationSec(target);
  if (flightDurationSec <= 0) return null;

  const elapsedSec = Math.max(0, Math.min(flightDurationSec, elapsed));
  const progress = elapsedSec / flightDurationSec;

  let value = 1 + progress * (target - 1);
  value = clampSyncedFlightValue(value, target);

  const step = multiplierToStep(value, target);
  const finalValue = clampSyncedFlightValue(
    +(1 + (step - 1) * STEP).toFixed(2),
    target
  );
  const finalStep = multiplierToStep(finalValue, target);

  return {
    step: finalStep,
    value: finalValue,
    elapsedMs: (finalStep - 1) * STEP_MS
  };
};

export default function AviatorX() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user);
  const [showRules, setShowRules] = useState(false);
  const [open, setOpen] = useState(false);
  const [scene, setScene] = useState("loading");
  const [launchCountdown, setLaunchCountdown] = useState(4);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [resultData, setResultData] = useState([]);
  const sessionUser = useSelector((s) => s.userStore.data);

  /** Audio */
  const bgAudioRef = useRef(null);
  const flewAwayAudioRef = useRef(null);
  const soundEnabledRef = useRef(false);

  const playIfSoundOn = (audioRef) => {
    if (!soundEnabledRef.current || !audioRef?.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play()?.catch(() => { });
  };

  const lastTenResults = useMemo(
    () => normalizeResultsList(resultData).slice(0, 10),
    [resultData]
  );

  /** Flight state */
  const [running, setRunning] = useState(false);
  const [flightValue, setFlightValue] = useState(0);

  /** Canvas refs */
  const canvasRef = useRef(null);
  const planeRef = useRef(null);

  const rafRef = useRef(null);
  const crashRafRef = useRef(null);
  const crashCanvasRafRef = useRef(null);
  const planePosRef = useRef({ x: 0, y: 0 });
  const startTsRef = useRef(null);
  const lastStepRef = useRef(-1);
  const skipStartHoldRef = useRef(false);
  const dotOffsetRef = useRef(0);
  const verticalDotOffsetRef = useRef(0);
  const rotationAngleRef = useRef(0);
  const planeHoverRef = useRef(createPlaneHoverState());
  const flightFrameTsRef = useRef(null);
  const counterRawRef = useRef(null);
  const capTsRef = useRef(0);
  const capFromRef = useRef(1);
  const capIntervalRef = useRef(SERVER_TICK_MS);

  const [scrollVersion, setScrollVersion] = useState(0);

  /** added by uk - show the server running_result. Do not build our own multiplier. Crash only when the round is CLOSED. */
  const maxFlightRef = useRef(null);
  const crashKnownRef = useRef(false);
  const lastServerRunningRef = useRef(1);
  /** ===== API / Betting state (LEFT side) ===== */
  const [leftBetAmount, setLeftBetAmount] = useState(100);
  const [leftBetErrorMessage, setLeftBetErrorMessage] = useState({});
  const [leftBetButtonShow, setLeftBetButtonShow] = useState(true);
  const [leftBetArray, setLeftBetArray] = useState([]);
  const leftBetArrayRef = useRef([]);
  const [leftMainBetId, setLeftMainBetId] = useState("");
  const [leftCashOutAmount, setLeftCashOutAmount] = useState("");
  const [leftShowCashoutButton, setLeftShowCashoutButton] = useState(false);
  const [leftIsCashingOut, setLeftIsCashingOut] = useState(false);
  const leftBetPlacedRef = useRef(false);
  const [leftBetGameId, setLeftBetGameId] = useState("");
  const leftMainBetIdRef = useRef("");
  const leftBetGameIdRef = useRef("");
  const leftBetAmountRef = useRef(leftBetAmount);
  const leftIsCashingOutRef = useRef(false);

  /** ===== API / Betting state (RIGHT side) ===== */
  const [rightBetAmount, setRightBetAmount] = useState(100);
  const [rightBetErrorMessage, setRightBetErrorMessage] = useState({});
  const [rightBetButtonShow, setRightBetButtonShow] = useState(true);
  const [rightBetArray, setRightBetArray] = useState([]);
  const rightBetArrayRef = useRef([]);
  const [rightMainBetId, setRightMainBetId] = useState("");
  const [rightCashOutAmount, setRightCashOutAmount] = useState("");
  const [rightShowCashoutButton, setRightShowCashoutButton] = useState(false);
  const [rightIsCashingOut, setRightIsCashingOut] = useState(false);
  const rightBetPlacedRef = useRef(false);
  const [rightBetGameId, setRightBetGameId] = useState("");
  const rightMainBetIdRef = useRef("");
  const rightBetGameIdRef = useRef("");
  const rightBetAmountRef = useRef(rightBetAmount);
  const rightIsCashingOutRef = useRef(false);
  const cashOutNowRef = useRef(null);

  /** ===== Auto Bet / Auto Cashout state ===== */
  const [leftAutoBet, setLeftAutoBet] = useState(false);
  const [rightAutoBet, setRightAutoBet] = useState(false);
  const [leftAutoCashout, setLeftAutoCashout] = useState(false);
  const [rightAutoCashout, setRightAutoCashout] = useState(false);
  const [leftAutoCashoutAt, setLeftAutoCashoutAt] = useState(1.1);
  const [rightAutoCashoutAt, setRightAutoCashoutAt] = useState(1.1);

  const leftAutoBetRef = useRef(false);
  const rightAutoBetRef = useRef(false);
  const leftAutoCashoutRef = useRef(false);
  const rightAutoCashoutRef = useRef(false);
  const leftAutoCashoutAtRef = useRef(1.1);
  const rightAutoCashoutAtRef = useRef(1.1);

  const tryAutoCashoutAtValue = (currentValue) => {
    const value = Number(currentValue);
    if (!(value + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    for (const side of ["left", "right"]) {
      const autoOn =
        side === "left"
          ? leftAutoCashoutRef.current
          : rightAutoCashoutRef.current;
      const autoAt = parseFloat(
        side === "left"
          ? leftAutoCashoutAtRef.current
          : rightAutoCashoutAtRef.current
      );
      const placed =
        side === "left" ? leftBetPlacedRef.current : rightBetPlacedRef.current;
      const cashing =
        side === "left"
          ? leftIsCashingOutRef.current
          : rightIsCashingOutRef.current;
      if (!autoOn || !placed || cashing) continue;
      if (!(autoAt > 0) || value + 0.0001 < autoAt) continue;
      cashOutNowRef.current?.(side, autoAt);
    }
  };

  const showLostToastForUncashedBets = (evtId = roundIdRef.current) => {
    if (evtId && lostToastEvtRef.current === evtId) return;

    let lostAmount = 0;
    if (leftBetPlacedRef.current && !leftIsCashingOutRef.current) {
      lostAmount += Number(leftBetAmountRef.current) || 0;
    }
    if (rightBetPlacedRef.current && !rightIsCashingOutRef.current) {
      lostAmount += Number(rightBetAmountRef.current) || 0;
    }
    if (lostAmount <= 0) return;

    if (evtId) lostToastEvtRef.current = evtId;
    pendingLostToastEvtRef.current = null;
    showLostToast(lostAmount);
  };

  /** ===== Shared state ===== */
  const [roundId, setRoundId] = useState("");
  const [userLuckyRecords, setUserLuckyRecords] = useState([]);
  const [liveBets, setLiveBets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const betsList = useMemo(
    () => [...userLuckyRecords, ...liveBets],
    [userLuckyRecords, liveBets]
  );
  const luckyPlayerCount = betsList.length;

  const isLoadGameRef = useRef(0);
  const lastCrashedEvtIdRef = useRef(null);
  const lastFinishedEvtIdRef = useRef(null);
  const roundIdRef = useRef("");
  const sceneRef = useRef("loading");
  const closedResetRef = useRef(null);
  const crashTimeoutRef = useRef(null);
  const lastLuckyPlayersEvtIdRef = useRef(null);
  const lastFlightStartedEvtIdRef = useRef(null);
  const lastCountdownResetEvtRef = useRef(null);
  const betPlacedToastEvtRef = useRef(null);
  const lostToastEvtRef = useRef(null);
  const pendingLostToastEvtRef = useRef(null);
  const placingBetsRef = useRef(false);
  const placeAllBetsNowRef = useRef(null);
  const luckyStreamTimerRef = useRef(null);
  const luckyCashoutTimerRef = useRef(null);
  const luckyCashoutPlanRef = useRef(null);
  const luckyPendingQueueRef = useRef([]);
  const userPlacedBetIdsRef = useRef(new Set());
  const userPlacedBetIdsOrderRef = useRef([]);
  const flightValueRef = useRef(flightValue);

  useEffect(() => {
    leftBetAmountRef.current = leftBetAmount;
  }, [leftBetAmount]);
  useEffect(() => {
    rightBetAmountRef.current = rightBetAmount;
  }, [rightBetAmount]);

  /** Track disconnection so we can reset on reconnect */
  const wasDisconnectedRef = useRef(false);
  const [activeTabIndex, setActiveTabIndex] = useState(1);

  /** Hard-reset everything back to waiting phase */
  const resetToWaiting = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (crashRafRef.current) {
      cancelAnimationFrame(crashRafRef.current);
      crashRafRef.current = null;
    }
    if (crashCanvasRafRef.current) {
      cancelAnimationFrame(crashCanvasRafRef.current);
      crashCanvasRafRef.current = null;
    }
    if (closedResetRef.current) {
      clearTimeout(closedResetRef.current);
      closedResetRef.current = null;
    }
    if (crashTimeoutRef.current) {
      clearTimeout(crashTimeoutRef.current);
      crashTimeoutRef.current = null;
    }
    planePosRef.current = { x: 0, y: 0 };
    dotOffsetRef.current = 0;
    verticalDotOffsetRef.current = 0;
    rotationAngleRef.current = 0;
    resetPlaneHoverState(planeHoverRef.current);
    flightFrameTsRef.current = null;
    bgAudioRef.current?.pause();
    setRunning(false);
    setFlightValue(0);
    maxFlightRef.current = null;
    crashKnownRef.current = false;
    lastServerRunningRef.current = 1;
    counterRawRef.current = null;
    capTsRef.current = 0;
    capFromRef.current = 1;
    capIntervalRef.current = SERVER_TICK_MS;
    setScrollVersion((v) => v + 1);
    setLeftBetButtonShow(true);
    setRightBetButtonShow(true);
    // NOTE: Do NOT clear lastFinishedEvtIdRef, lastFlightStartedEvtIdRef, or isLoadGameRef here.
    setScene("waiting");
    sceneRef.current = "waiting";
  };

  /** ===== Socket ===== */
  const { data, isConnected } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "aviatorx"
  });

  //console.log("data inside AviatorX: ", data?.data?.data);

  // ===== Socket reconnect handling =====
  useEffect(() => {
    if (!isConnected) {
      wasDisconnectedRef.current = true;
      // Pause the flight ticker while disconnected
      setRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    // Socket just came back online
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false;
      //console.log("Socket reconnected – resetting AviatorX state");

      // Reset the game to the loading state so server data drives the next scene
      setFlightValue(0);
      setScene("loading");
      sceneRef.current = "loading";
      isLoadGameRef.current = 0;
      lastCrashedEvtIdRef.current = null;
      lastFinishedEvtIdRef.current = null;
      //added by uk - the socket drop left this round marked as already flying, so the plane never started again.
      lastFlightStartedEvtIdRef.current = null;
      crashKnownRef.current = false;
      planePosRef.current = { x: 0, y: 0 };
      dotOffsetRef.current = 0;
      verticalDotOffsetRef.current = 0;
      rotationAngleRef.current = 0;
      resetPlaneHoverState(planeHoverRef.current);
      flightFrameTsRef.current = null;
      setScrollVersion((v) => v + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  const rawInner = data?.data;
  const payload = rawInner?.data?.evt_id
    ? rawInner.data
    : rawInner?.evt_id
      ? rawInner
      : null;
  const payloadLuckyPlayers = rawInner?.data?.evt_id
    ? rawInner.lucky_players
    : data?.lucky_players;

  const payloadResult = data?.data?.result;
  const userName = userInfo?.data?.uname || data?.user?.uname || "You";

  const clearLuckyPlayersStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
    if (luckyCashoutTimerRef.current) {
      clearInterval(luckyCashoutTimerRef.current);
      luckyCashoutTimerRef.current = null;
    }
  };

  const isInFlightScene = () =>
    sceneRef.current === "flight" || sceneRef.current === "crashed";

  const getPreFlightStreamDelay = (remainingCount) => {
    const base = Math.max(
      20,
      Math.floor(PRE_FLIGHT_STREAM_WINDOW_MS / Math.max(remainingCount, 1))
    );
    return luckyRandomInt(Math.max(15, base - 10), base + 20);
  };

  const flushRemainingDummyBets = () => {
    const queue = luckyPendingQueueRef.current;
    if (!queue.length) return;
    luckyPendingQueueRef.current = [];
    setLiveBets((prev) => [...queue, ...prev]);
  };

  const stopDummyBetStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
  };

  const finalizeDummyBetsBeforeFlight = () => {
    stopDummyBetStream();
    flushRemainingDummyBets();
  };

  const scheduleNextLiveBet = () => {
    if (isInFlightScene()) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const queue = luckyPendingQueueRef.current;
    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const batchSize = luckyRandomInt(2, 5);
    const batch = queue.splice(0, Math.min(batchSize, queue.length));
    setLiveBets((prev) => [...batch, ...prev]);

    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const delay = getPreFlightStreamDelay(queue.length);
    luckyStreamTimerRef.current = setTimeout(scheduleNextLiveBet, delay);
  };

  const startLiveLuckyPlayersStream = () => {
    clearLuckyPlayersStream();
    luckyPendingQueueRef.current = createDummyBetQueue();
    userPlacedBetIdsRef.current = new Set();
    userPlacedBetIdsOrderRef.current = [];
    setUserLuckyRecords([]);
    setLiveBets([]);
    scheduleNextLiveBet();
  };

  const beginFlight = ({
    target,
    leftTime,
    runningResult,
    syncProgress = false,
    force = false
  }) => {
    const evtId = roundIdRef.current;
    const postCrashScenes = ["crashed", "finished"];

    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (evtId && lastFlightStartedEvtIdRef.current === evtId) return;
    if (!force && postCrashScenes.includes(sceneRef.current)) return;

    lastFlightStartedEvtIdRef.current = evtId;
    luckyCashoutPlanRef.current = null;
    finalizeDummyBetsBeforeFlight();

    crashKnownRef.current = false;
    const running = parseFloat(runningResult);
    const startValue = Number.isFinite(running) && running >= 1 ? running : 1;
    lastServerRunningRef.current = startValue;
    maxFlightRef.current = startValue;

    const isLateJoin = Number.isFinite(running) && running > 1.15;
    skipStartHoldRef.current = !!isLateJoin;

    dotOffsetRef.current = 0;
    verticalDotOffsetRef.current = 0;
    rotationAngleRef.current = 0;
    resetPlaneHoverState(planeHoverRef.current);
    flightFrameTsRef.current = null;

    capTsRef.current = performance.now();
    capFromRef.current = startValue;
    capIntervalRef.current = SERVER_TICK_MS;

    if (isLateJoin) {
      flightValueRef.current = startValue;
      counterRawRef.current = startValue;
      setFlightValue(startValue);
      lastStepRef.current = multiplierToStep(startValue, startValue);
      startTsRef.current =
        performance.now() -
        elapsedMsFromMultiplier(startValue, skipStartHoldRef.current);
    } else {
      flightValueRef.current = 1;
      counterRawRef.current = 1;
      setFlightValue(1);
      lastStepRef.current = -1;
      startTsRef.current = null;
    }

    setScrollVersion((v) => v + 1);
    setScene("flight");
    sceneRef.current = "flight";
    setRunning(true);
  };

  const startTakeoffAfterBets = async ({
    target,
    leftTime,
    runningResult,
    syncProgress = false,
    force = false
  }) => {
    const evtId = roundIdRef.current;
    const evtStatus = payload?.evt_status ?? payload?.status;
    if (evtStatus === "CLOSED") return;
    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (!force && evtId && lastFlightStartedEvtIdRef.current === evtId) return;

    beginFlight({
      target,
      leftTime,
      runningResult,
      syncProgress,
      force
    });
    if (soundEnabledRef.current) {
      bgAudioRef.current?.play()?.catch(() => { });
    } else {
      bgAudioRef.current?.pause();
    }
  };

  const updateUserData = async (userData) => {
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };

  /** ===== Sync payload -> state ===== */
  useEffect(() => {
    if (!payload) return;

    updateUserData(data?.user);

    // Skip payloads if the evt_id is the same as the last finished round
    if (payload.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
      return;

    if (payload.evt_id) {
      setRoundId(payload.evt_id);
      roundIdRef.current = payload.evt_id;
    }

    // Bet button enable/disable based on game state
    const evtStatus = payload.evt_status ?? payload.status;
    if (payload.start == 0) {
      const incomingEvtId = payload.evt_id;
      const flyingThisRound =
        incomingEvtId &&
        lastFlightStartedEvtIdRef.current &&
        String(lastFlightStartedEvtIdRef.current) === String(incomingEvtId) &&
        (sceneRef.current === "flight" || sceneRef.current === "crashed");
      // A late start==0 for the round that already took off would hide Cashout
      // for about a second and then bring it back. Ignore it.
      if (flyingThisRound) {
        return;
      }
      const isNewCountdownRound =
        incomingEvtId && incomingEvtId !== lastCountdownResetEvtRef.current;

      // Next evt can open while this client is still flying/crashing the last one.
      // Close that flight against the old id so it cannot mark the new round finished.
      if (isNewCountdownRound && sceneRef.current === "flight") {
        const flyingEvtId = lastFlightStartedEvtIdRef.current;
        if (flyingEvtId && flyingEvtId !== incomingEvtId) {
          lastFinishedEvtIdRef.current = flyingEvtId;
          lastCrashedEvtIdRef.current = flyingEvtId;
          const result = maxFlightRef.current;
          if (result != null && result > 0) {
            setResultData((prev) => {
              const list = normalizeResultsList(prev);
              if (list.length > 0 && list[list.length - 1] === result) {
                return prev;
              }
              return [...list, result];
            });
          }
          pendingLostToastEvtRef.current = flyingEvtId;
          showLostToastForUncashedBets(flyingEvtId);
        }
        setRunning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }

      lastFlightStartedEvtIdRef.current = null;
      if (lastCrashedEvtIdRef.current === incomingEvtId) {
        lastCrashedEvtIdRef.current = null;
      }
      crashKnownRef.current = false;
      lastServerRunningRef.current = 1;
      counterRawRef.current = null;
      capTsRef.current = 0;
      capFromRef.current = 1;
      capIntervalRef.current = SERVER_TICK_MS;
      if (closedResetRef.current) {
        clearTimeout(closedResetRef.current);
        closedResetRef.current = null;
      }
      if (crashTimeoutRef.current) {
        clearTimeout(crashTimeoutRef.current);
        crashTimeoutRef.current = null;
      }

      if (
        sceneRef.current === "loading" ||
        sceneRef.current === "waiting" ||
        sceneRef.current === "finished" ||
        sceneRef.current === "crashed" ||
        sceneRef.current === "flight"
      ) {
        setLaunchCountdown(LAUNCH_COUNTDOWN_DEFAULT);
        setFlightValue(0);
        setScrollVersion((v) => v + 1);
        setScene("countdown");
        sceneRef.current = "countdown";
      }

      if (isNewCountdownRound) {
        lastCountdownResetEvtRef.current = payload.evt_id;
        betPlacedToastEvtRef.current = null;
        lostToastEvtRef.current = null;

        setLeftBetButtonShow(true);
        setLeftShowCashoutButton(false);
        setLeftMainBetId("");
        leftMainBetIdRef.current = "";
        setLeftCashOutAmount("");
        leftBetPlacedRef.current = false;
        setLeftBetGameId("");
        leftBetGameIdRef.current = "";
        if (leftBetArrayRef.current.length === 0) setLeftBetErrorMessage({});

        setRightBetButtonShow(true);
        setRightShowCashoutButton(false);
        setRightMainBetId("");
        rightMainBetIdRef.current = "";
        setRightCashOutAmount("");
        rightBetPlacedRef.current = false;
        setRightBetGameId("");
        rightBetGameIdRef.current = "";
        if (rightBetArrayRef.current.length === 0) setRightBetErrorMessage({});
      }

      if (
        payload.evt_id &&
        payload.evt_id !== lastLuckyPlayersEvtIdRef.current
      ) {
        lastLuckyPlayersEvtIdRef.current = payload.evt_id;
        startLiveLuckyPlayersStream();
      }

      // Auto bet: queue once per round (avoid re-firing on every countdown tick)
      if (
        isNewCountdownRound &&
        leftAutoBetRef.current &&
        leftBetArrayRef.current.length === 0 &&
        !leftBetPlacedRef.current
      ) {
        handleBetClick("left");
      }
      if (
        isNewCountdownRound &&
        rightAutoBetRef.current &&
        rightBetArrayRef.current.length === 0 &&
        !rightBetPlacedRef.current
      ) {
        handleBetClick("right");
      }

      if (isNewCountdownRound) {
        const flushLeft =
          leftBetArrayRef.current.length > 0 && !leftBetPlacedRef.current;
        const flushRight =
          rightBetArrayRef.current.length > 0 && !rightBetPlacedRef.current;
        if (flushLeft) {
          setLeftBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushRight) {
          setRightBetErrorMessage({ status: true, message: "Placing bet..." });
        }
        if (flushLeft || flushRight) {
          placeAllBetsNowRef.current?.(flushLeft, flushRight);
        }
      }
    } else if (payload.start == 1 && evtStatus !== "CLOSED") {
      // AX-1: join from running_result. Crash is not on the live feed.
      if (
        (sceneRef.current === "loading" || sceneRef.current === "waiting") &&
        lastFlightStartedEvtIdRef.current !== payload.evt_id
      ) {
        startTakeoffAfterBets({
          target: parseFloat(payload.running_result) || 1,
          runningResult: payload.running_result,
          leftTime: payload.left_time,
          syncProgress: true
        });
      }

      if (
        !leftIsCashingOutRef.current &&
        (leftBetArrayRef.current.length > 0 || leftBetPlacedRef.current)
      ) {
        setLeftBetButtonShow(false);
      }
      if (
        !rightIsCashingOutRef.current &&
        (rightBetArrayRef.current.length > 0 || rightBetPlacedRef.current)
      ) {
        setRightBetButtonShow(false);
      }

      isLoadGameRef.current = 1;

      if (
        !leftIsCashingOutRef.current &&
        leftBetPlacedRef.current &&
        (leftMainBetId || leftMainBetIdRef.current)
      ) {
        setLeftShowCashoutButton(true);
      }
      if (
        !rightIsCashingOutRef.current &&
        rightBetPlacedRef.current &&
        (rightMainBetId || rightMainBetIdRef.current)
      ) {
        setRightShowCashoutButton(true);
      }
    }

    // Reset isLoadGame on new countdown
    if (payload.start == 0) {
      isLoadGameRef.current = 0;
    }
  }, [payload]);

  useEffect(() => {
    if (!payload || payload.start != 1) return;
    const evtStatus = payload.evt_status ?? payload.status;
    if (evtStatus === "CLOSED") return;
    const srv = parseFloat(payload.running_result);
    if (!Number.isFinite(srv) || srv < 1) return;

    const nowTs = performance.now();
    const shown = Number.isFinite(counterRawRef.current)
      ? counterRawRef.current
      : 1;
    if (capTsRef.current > 0 && srv > lastServerRunningRef.current + 0.0001) {
      const measured = nowTs - capTsRef.current;
      if (measured >= 20 && measured <= 1500) {
        capIntervalRef.current = measured;
      }
    }
    capTsRef.current = nowTs;
    //added by uk - do not jump straight to the new server number.
    //added by uk - start from the number already on screen and climb up to it. Same filler as Aviator.
    capFromRef.current = Math.min(shown, srv);
    lastServerRunningRef.current = srv;
    maxFlightRef.current = srv;
  }, [
    payload?.running_result,
    payload?.start,
    payload?.evt_status,
    payload?.evt_id
  ]);

  const activeTab = async (tab1) => {
    setActiveTabIndex(tab1);
    if (tab1 == 2) {
    }
  };

  useEffect(() => {
    if (payloadResult !== null && payloadResult !== undefined) {
      setResultData(payloadResult);
    }
  }, [payloadResult]);

  /** ===== Lucky Players: stream in live-style (100–180), user bet pinned at top ===== */
  useEffect(() => {
    startLiveLuckyPlayersStream();
    return () => clearLuckyPlayersStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Sync placed user bets from socket lucky_players (actual server bet records) */
  useEffect(() => {
    const placedOrder = userPlacedBetIdsOrderRef.current;
    if (
      !placedOrder.length ||
      !payloadLuckyPlayers ||
      typeof payloadLuckyPlayers !== "object"
    ) {
      return;
    }

    setUserLuckyRecords((prev) => {
      const next = placedOrder
        .map((betId) => {
          const existing = prev.find(
            (bet) => String(bet.betId) === String(betId)
          );
          if (existing?.locked) return existing;

          const entry = payloadLuckyPlayers[betId];
          if (entry) {
            const parsed = parseLuckyPlayerEntry(entry, {
              isUser: true,
              betId
            });
            if (parsed) {
              if (sceneRef.current !== "flight" || !parsed.locked) {
                return {
                  ...parsed,
                  rate: "--",
                  profit: "",
                  locked: false
                };
              }
              return parsed;
            }
          }
          return existing;
        })
        .filter(Boolean);

      return next.length ? next : prev;
    });
  }, [payloadLuckyPlayers]);

  useEffect(() => {
    flightValueRef.current = flightValue;
  }, [flightValue]);

  /** During flight: cash out dummy bets on a schedule spread across the round */
  useEffect(() => {
    if (scene !== "flight" || flightValue < 1) {
      if (scene !== "flight") luckyCashoutPlanRef.current = null;
      return;
    }

    if (!luckyCashoutPlanRef.current) {
      setLiveBets((prev) => {
        if (prev.length > 0) {
          luckyCashoutPlanRef.current = buildLuckyCashoutPlan(
            LUCKY_PLAN_TARGET,
            prev.length
          );
        }
        return prev;
      });
      if (!luckyCashoutPlanRef.current) return;
    }

    const plan = luckyCashoutPlanRef.current;
    const pendingEvents = [];

    while (
      plan.nextEventIndex < plan.events.length &&
      flightValue + 0.0001 >= plan.events[plan.nextEventIndex].value
    ) {
      pendingEvents.push(plan.events[plan.nextEventIndex]);
      plan.nextEventIndex += 1;
    }

    if (!pendingEvents.length) return;

    setLiveBets((prev) =>
      applyLuckyCashoutEvents(prev, pendingEvents, plan.reserveCount)
    );
  }, [scene, flightValue]);

  // Keep sceneRef in sync
  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  /** ===== When status becomes CLOSED, trigger crash immediately (once per evt_id) ===== */
  useEffect(() => {
    const status = payload?.evt_status ?? payload?.status;
    const evtId = payload?.evt_id;
    if (
      status === "CLOSED" &&
      evtId &&
      lastCrashedEvtIdRef.current !== evtId &&
      (sceneRef.current === "flight" || sceneRef.current === "countdown")
    ) {
      const result = parseFloat(payload?.result);
      if (!Number.isFinite(result) || result <= 0) return; // wait until result is public

      lastCrashedEvtIdRef.current = evtId;
      crashKnownRef.current = true;
      maxFlightRef.current = result;
      lastServerRunningRef.current = Math.max(
        lastServerRunningRef.current || 1,
        result
      );

      //added by uk - when the round closes, show the crash right away. The old code waited and caught up, which looked like a hang.
      counterRawRef.current = result;
      flightValueRef.current = result;
      lastStepRef.current = multiplierToStep(result, result);
      setFlightValue(result);
      triggerCrash();

      // Schedule a hard reset 2s after CLOSED, regardless of animation state
      if (closedResetRef.current) clearTimeout(closedResetRef.current);
      closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.evt_status, payload?.status, payload?.evt_id, payload?.result]);

  /** ===== Countdown ===== */
  useEffect(() => {
    if (scene !== "countdown") return;

    // Take off the moment the server does, even if the local countdown has time
    // left. Short rounds are over in a second and cannot wait for it.
    if (payload?.start == 1) {
      // Don't replay a round that already finished — wait for a new evt_id
      if (payload?.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
        return;
      // Don't start flight if the current event is already CLOSED
      const evtStatus = payload?.evt_status ?? payload?.status;
      if (evtStatus === "CLOSED") return;
      if (lastFlightStartedEvtIdRef.current === payload?.evt_id) return;

      startTakeoffAfterBets({
        target: parseFloat(payload?.running_result) || 1,
        runningResult: payload?.running_result,
        syncProgress: true
      });
      return;
    }

    if (launchCountdown <= 0) return; // wait for the server to start the round

    const t = setTimeout(() => setLaunchCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [scene, launchCountdown, payload?.start, payload?.running_result]);

  /** ===== Flight ticker with canvas animation ===== */
  useEffect(() => {
    if (scene !== "flight" || !running) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = canvas.parentElement.clientHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Track whether the actual counting has started (target became valid)
    let countingStarted = false;

    const tick = (now) => {
      const evtId = roundIdRef.current;
      if (evtId && lastFinishedEvtIdRef.current === evtId) {
        setRunning(false);
        return;
      }

      const target = maxFlightRef.current;

      // Spin-wait until maxFlightRef has a valid value
      if (target == null || target <= 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // First tick — fresh flight or mid-round join (startTsRef pre-set)
      if (!countingStarted) {
        countingStarted = true;
        resetPlaneHoverState(planeHoverRef.current);
        flightFrameTsRef.current = null;
        if (lastStepRef.current < 0) {
          startTsRef.current = now;
          lastStepRef.current = 0;
        } else {
          startTsRef.current =
            startTsRef.current ?? now - (lastStepRef.current - 1) * STEP_MS;
        }
      }

      const elapsed = now - startTsRef.current;
      const cap = lastServerRunningRef.current;
      const safeCap = Number.isFinite(cap) && cap >= 1 ? cap : 1;

      // Animate from where the counter stood when this running_result arrived up
      // to running_result itself, spread over one server tick. running_result is
      // a ceiling the counter is never allowed to pass.
      const from = Math.max(
        1,
        Number.isFinite(capFromRef.current) ? capFromRef.current : safeCap
      );
      const sinceCap = Math.max(0, now - (capTsRef.current || now));
      //added by uk - filler. Walk one cent at a time up to the server number.
      //added by uk - 200ms per cent until 1.10, then 91ms. Same speed as Aviator.
      const cents = Math.max(0, Math.round((safeCap - from) * 100));
      const stepMs = safeCap <= 1.1 ? 200 : 91;
      const catchWindowMs = Math.max(40, cents * stepMs);
      const progress = Math.min(1, sinceCap / catchWindowMs);
      let raw = from + (safeCap - from) * progress;
      raw = Math.min(safeCap, raw);
      const prevRaw = counterRawRef.current;
      if (Number.isFinite(prevRaw) && prevRaw > raw) raw = Math.min(prevRaw, safeCap);

      counterRawRef.current = raw;
      const next = Math.max(1, Math.floor(raw * 100) / 100);
      flightValueRef.current = next;

      // Never claim above what the server has published.
      tryAutoCashoutAtValue(Math.min(next, safeCap));

      // Hold at running_result until CLOSED publishes result. Do not crash on the cap.
      if (crashKnownRef.current && next + 0.001 >= (target || safeCap)) {
        lastStepRef.current = multiplierToStep(
          target || safeCap,
          target || safeCap
        );
        flightValueRef.current = target || safeCap;
        setFlightValue(target || safeCap);
        triggerCrash();
        return;
      }

      const steps = multiplierToStep(next, Math.max(safeCap, next));
      if (steps !== lastStepRef.current) {
        lastStepRef.current = steps;
        flightValueRef.current = next;
        setFlightValue(next);
      }

      const W = canvas.width;
      const H = canvas.height;
      if (W === 0 || H === 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      ctx.clearRect(0, 0, W, H);
      drawAvxRotatingBackground(ctx, W, H, rotationAngleRef);
      drawAvxCenteredGradient(ctx, W, H);

      // Plane climb starts with the slow 1.00 → 1.10 counter
      const climbElapsed = elapsed;
      const climbProgress = Math.min(1, climbElapsed / CLIMB_DURATION);

      const PLANE_W = 80;

      const frameDeltaMs =
        flightFrameTsRef.current == null
          ? 16
          : Math.min(32, Math.max(8, now - flightFrameTsRef.current));
      flightFrameTsRef.current = now;

      const { tipX: rawTipX, tipY: rawTipY } = getPlaneFlightTip(
        elapsed,
        climbProgress,
        W,
        H,
        planeHoverRef.current,
        frameDeltaMs
      );
      let tipX = rawTipX;
      let tipY = rawTipY;

      tipY = Math.max(PLANE_W, tipY);
      tipX = Math.max(0, Math.min(W, tipX));

      const controlX = tipX / 2;
      const controlY = H + 2;

      // Red fill area under curve
      const grad = ctx.createLinearGradient(0, H, 0, 0);
      grad.addColorStop(0, "rgba(229, 5, 57, 0.1)");
      grad.addColorStop(1, "rgba(229, 5, 57, 0.6)");
      ctx.beginPath();
      ctx.moveTo(0, H);
      ctx.quadraticCurveTo(controlX, controlY, tipX, tipY);
      ctx.lineTo(tipX, H);
      ctx.lineTo(0, H);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // Line (quadratic bezier to plane tip)
      ctx.beginPath();
      ctx.moveTo(0, H);
      ctx.quadraticCurveTo(controlX, controlY, tipX, tipY);
      ctx.strokeStyle = "#E50539";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.stroke();

      // Tip dot
      ctx.beginPath();
      ctx.arc(tipX, tipY, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.shadowColor = "#E50539";
      ctx.shadowBlur = 12;
      ctx.fill();
      ctx.shadowBlur = 0;

      drawAvxMovingDots(ctx, W, H, dotOffsetRef.current);
      drawAvxVerticalDots(ctx, W, H, verticalDotOffsetRef.current);
      drawAvxDividerLines(ctx, W, H);

      // Scroll axis dots inward only after the plane reaches the top
      if (climbProgress >= 1) {
        const nextOffsets = advanceAxisDotOffsets(
          dotOffsetRef.current,
          verticalDotOffsetRef.current
        );
        dotOffsetRef.current = nextOffsets.dotOffset;
        verticalDotOffsetRef.current = nextOffsets.verticalDotOffset;
      }

      // Position plane at the tip of the curve
      planePosRef.current = {
        x: tipX - PLANE_W / 2,
        y: tipY - PLANE_W * 0.55
      };
      if (planeRef.current) {
        planeRef.current.style.transform = `translate(${tipX - PLANE_W / 2
          }px, ${tipY - PLANE_W * 0.55}px)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, running]);

  /** ===== Crash pipeline ===== */
  const triggerCrash = () => {
    const crashedEvtId =
      lastFlightStartedEvtIdRef.current || roundIdRef.current;
    if (crashedEvtId) {
      lastFinishedEvtIdRef.current = crashedEvtId;
    }
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const result = maxFlightRef.current;
    if (result != null && result > 0) {
      setResultData((prev) => {
        const list = normalizeResultsList(prev);
        if (list.length > 0 && list[list.length - 1] === result) return prev;
        return [...list, result];
      });
    }

    setScene("crashed");
    sceneRef.current = "crashed";
    bgAudioRef.current?.pause();
    playIfSoundOn(flewAwayAudioRef);

    tryAutoCashoutAtValue(result);
    pendingLostToastEvtRef.current = crashedEvtId;
    showLostToastForUncashedBets(crashedEvtId);

    // Hard reset 2s after crash — overrides display timeout
    if (closedResetRef.current) clearTimeout(closedResetRef.current);
    closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);

    // Fallback: if CLOSED reset didn't fire, reset after CRASHED_DISPLAY_MS
    crashTimeoutRef.current = setTimeout(() => {
      resetToWaiting();
    }, CRASHED_DISPLAY_MS);
  };

  // Crash fly-off animation — plane flies to upper-right; canvas shows grayscale scene
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (scene !== "crashed") {
      if (crashRafRef.current) cancelAnimationFrame(crashRafRef.current);
      if (crashCanvasRafRef.current)
        cancelAnimationFrame(crashCanvasRafRef.current);
      return;
    }
    const plane = planeRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const paintCrashedCanvas = () => {
      const W = canvas.width;
      const H = canvas.height;
      if (W > 0 && H > 0) {
        drawAvxCrashedCanvas(
          ctx,
          W,
          H,
          rotationAngleRef,
          dotOffsetRef.current,
          verticalDotOffsetRef.current
        );
      }
      crashCanvasRafRef.current = requestAnimationFrame(paintCrashedCanvas);
    };
    paintCrashedCanvas();

    if (!plane) {
      return () => {
        if (crashCanvasRafRef.current)
          cancelAnimationFrame(crashCanvasRafRef.current);
      };
    }

    const dX = canvas.width * 0.015;
    const dY = canvas.height * 0.015;
    let pos = { ...planePosRef.current };

    const fly = () => {
      pos.x += dX;
      pos.y -= dY;
      // Once off-screen, hide the plane
      if (pos.x > canvas.width + 200 || pos.y < -200) {
        plane.style.transform = `translate(-999px, -999px)`;
        plane.style.opacity = "0";
        return;
      }
      plane.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      crashRafRef.current = requestAnimationFrame(fly);
    };

    // Remove the smooth transition so fly-off is frame-accurate
    plane.style.transition = "none";
    plane.style.opacity = "1";
    crashRafRef.current = requestAnimationFrame(fly);
    return () => {
      if (crashRafRef.current) cancelAnimationFrame(crashRafRef.current);
      if (crashCanvasRafRef.current)
        cancelAnimationFrame(crashCanvasRafRef.current);
    };
  }, [scene]);

  /** ===== Sound toggle ===== */
  const toggleSound = (e) => {
    const on = e.target.checked;
    soundEnabledRef.current = on;
    setSoundEnabled(on);
    if (on) {
      if (sceneRef.current === "flight") {
        bgAudioRef.current?.play()?.catch(() => { });
      }
    } else {
      bgAudioRef.current?.pause();
      flewAwayAudioRef.current?.pause();
    }
  };

  /** ===== Handle Bet Click ===== */
  const handleBetClick = (side) => {
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const bettype = "b";
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;

    if (!betAmount || betAmount <= 0) return;

    const liveOdds = Number(payload?.b1);
    const betObj = {
      amount: Number(betAmount),
      profit: 0,
      catmid: payload?.evt_id || roundIdRef.current || roundId,
      round: payload?.evt_id || roundIdRef.current || roundId,
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      teamname: "aviatorx",
      bettype: bettype,
      marketVal: 1,
      game_type: 7,
      market_type: "m",
      game_name: "aviatorx"
    };

    const newBet = { ...betObj, is_bet: false };
    betArrayRef.current = [...betArrayRef.current, newBet];
    setBetArray([...betArrayRef.current]);

    if (payload?.start == 0) {
      setBetErrorMessage({
        status: true,
        message: "Placing bet..."
      });
      placeAllBetsNowRef.current?.(side === "left", side === "right");
    } else {
      setBetErrorMessage({
        status: true,
        message: "Bet queued for next round"
      });
    }
  };

  /** ===== Place all queued bets in a single API call ===== */
  const placeAllBetsNow = async (hasLeft, hasRight) => {
    if (placingBetsRef.current) return;

    const placedEvtId = roundIdRef.current;
    const combinedBetArray = [];
    let leftBetObj = null;
    let rightBetObj = null;
    let leftBetsSnapshot = [];
    let rightBetsSnapshot = [];

    if (hasLeft) {
      leftBetsSnapshot = [...leftBetArrayRef.current];
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      for (const bet of leftBetsSnapshot) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = placedEvtId || bet.catmid;
          bet.round = placedEvtId || bet.round;
          leftBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 0
          });
        }
      }
    }

    if (hasRight) {
      rightBetsSnapshot = [...rightBetArrayRef.current];
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      for (const bet of rightBetsSnapshot) {
        if (!bet.is_bet) {
          bet.is_bet = true;
          bet.catmid = placedEvtId || bet.catmid;
          bet.round = placedEvtId || bet.round;
          rightBetObj = bet;
          combinedBetArray.push({
            bet_type: bet.game_type,
            bet_amount: bet.amount,
            section_no: 0
          });
        }
      }
    }

    if (combinedBetArray.length === 0) return;
    placingBetsRef.current = true;

    const restoreQueuedBets = () => {
      if (hasLeft && leftBetsSnapshot.length) {
        const restored = leftBetsSnapshot.map((bet) => ({
          ...bet,
          is_bet: false,
          catmid: placedEvtId || bet.catmid,
          round: placedEvtId || bet.round
        }));
        leftBetArrayRef.current = restored;
        setLeftBetArray(restored);
      }
      if (hasRight && rightBetsSnapshot.length) {
        const restored = rightBetsSnapshot.map((bet) => ({
          ...bet,
          is_bet: false,
          catmid: placedEvtId || bet.catmid,
          round: placedEvtId || bet.round
        }));
        rightBetArrayRef.current = restored;
        setRightBetArray(restored);
      }
    };

    const baseBet = leftBetObj || rightBetObj;
    const liveOdds = Number(baseBet?.odds);
    const requestBody = {
      ...baseBet,
      amount: Number(baseBet?.amount),
      teamname: "aviatorx",
      bettype: baseBet?.bettype || "b",
      market_type: "m",
      game_name: "aviatorx",
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      catmid: roundIdRef.current || baseBet.catmid,
      round: roundIdRef.current || baseBet.round,
      bet_array: combinedBetArray
    };

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino",
        requestBody
      );

      if (response.data?.status != null) {
        if (response.data.status) {
          const returnBets = response.data?.data?.return_bets;
          const betsArray = Array.isArray(returnBets)
            ? returnBets
            : returnBets?.bet_id
              ? [returnBets]
              : [];

          let anyBetConfirmed = false;
          if (hasLeft && hasRight) {
            if (betsArray[0]?.bet_id) {
              leftMainBetIdRef.current = betsArray[0].bet_id;
              leftBetGameIdRef.current = roundIdRef.current || roundId;
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
              anyBetConfirmed = true;
              showBetPlacedToast();
            }
            if (betsArray[1]?.bet_id) {
              rightMainBetIdRef.current = betsArray[1].bet_id;
              rightBetGameIdRef.current = roundIdRef.current || roundId;
              setRightMainBetId(betsArray[1].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
              anyBetConfirmed = true;
              showBetPlacedToast();
            }
          } else if (hasLeft) {
            if (betsArray[0]?.bet_id) {
              leftMainBetIdRef.current = betsArray[0].bet_id;
              leftBetGameIdRef.current = roundIdRef.current || roundId;
              setLeftMainBetId(betsArray[0].bet_id);
              setLeftBetGameId(roundIdRef.current || roundId);
              leftBetPlacedRef.current = true;
              setLeftShowCashoutButton(true);
              setLeftBetButtonShow(false);
              anyBetConfirmed = true;
            }
          } else if (hasRight) {
            if (betsArray[0]?.bet_id) {
              rightMainBetIdRef.current = betsArray[0].bet_id;
              rightBetGameIdRef.current = roundIdRef.current || roundId;
              setRightMainBetId(betsArray[0].bet_id);
              setRightBetGameId(roundIdRef.current || roundId);
              rightBetPlacedRef.current = true;
              setRightShowCashoutButton(true);
              setRightBetButtonShow(false);
              anyBetConfirmed = true;
            }
          }

          const placedAmounts = [];
          if (hasLeft && hasRight) {
            if (leftBetObj) placedAmounts.push(leftBetObj.amount);
            if (rightBetObj) placedAmounts.push(rightBetObj.amount);
          } else if (hasLeft && leftBetObj) {
            placedAmounts.push(leftBetObj.amount);
          } else if (hasRight && rightBetObj) {
            placedAmounts.push(rightBetObj.amount);
          }

          const userRecords = buildUserRecordsFromReturnBets(
            betsArray,
            placedAmounts,
            userName
          );
          if (userRecords.length) {
            userPlacedBetIdsOrderRef.current = userRecords.map(
              (record) => record.betId
            );
            userPlacedBetIdsRef.current = new Set(
              userPlacedBetIdsOrderRef.current.map(String)
            );
            setUserLuckyRecords(userRecords);
          }

          if (
            anyBetConfirmed &&
            roundIdRef.current &&
            betPlacedToastEvtRef.current !== roundIdRef.current
          ) {
            betPlacedToastEvtRef.current = roundIdRef.current;
            //showBetPlacedToast();
            if (hasLeft) setLeftBetErrorMessage({});
            if (hasRight) setRightBetErrorMessage({});
          }
          if (anyBetConfirmed) {
            const roundAlreadyOver =
              pendingLostToastEvtRef.current === placedEvtId ||
              lastFinishedEvtIdRef.current === placedEvtId;
            if (roundAlreadyOver) {
              showLostToastForUncashedBets(placedEvtId);
            } else {
              tryAutoCashoutAtValue(flightValueRef.current);
            }
          } else {
            restoreQueuedBets();
          }
        } else {
          restoreQueuedBets();
          const msg = { status: false, message: response.data.message };
          if (hasLeft) setLeftBetErrorMessage(msg);
          if (hasRight) setRightBetErrorMessage(msg);
        }
      } else {
        restoreQueuedBets();
      }
    } catch (error) {
      restoreQueuedBets();
      const errMsg = {
        status: false,
        message: error.response?.data?.message || "Bet placement failed"
      };
      if (hasLeft) setLeftBetErrorMessage(errMsg);
      if (hasRight) setRightBetErrorMessage(errMsg);
    } finally {
      placingBetsRef.current = false;
    }
  };
  placeAllBetsNowRef.current = placeAllBetsNow;

  /** ===== Cancel queued bet ===== */
  const cancelBet = (side) => {
    if (side === "left") {
      leftBetArrayRef.current = [];
      setLeftBetArray([]);
      setLeftBetErrorMessage({ status: false, message: "Bet cancelled" });
    } else {
      rightBetArrayRef.current = [];
      setRightBetArray([]);
      setRightBetErrorMessage({ status: false, message: "Bet cancelled" });
    }
  };

  /** ===== Cashout handler ===== */
  const cashOutNow = async (side, multiplierOverride) => {
    const isCashingOutRef =
      side === "left" ? leftIsCashingOutRef : rightIsCashingOutRef;
    const mainBetIdRef = side === "left" ? leftMainBetIdRef : rightMainBetIdRef;
    const betGameIdRef = side === "left" ? leftBetGameIdRef : rightBetGameIdRef;
    const mainBetId =
      mainBetIdRef.current ||
      (side === "left" ? leftMainBetId : rightMainBetId);
    const betAmount = side === "left" ? leftBetAmount : rightBetAmount;
    const setIsCashingOut =
      side === "left" ? setLeftIsCashingOut : setRightIsCashingOut;
    const setShowCashoutButton =
      side === "left" ? setLeftShowCashoutButton : setRightShowCashoutButton;
    const setMainBetId = side === "left" ? setLeftMainBetId : setRightMainBetId;
    const betPlacedRef = side === "left" ? leftBetPlacedRef : rightBetPlacedRef;
    const setBetErrorMessage =
      side === "left" ? setLeftBetErrorMessage : setRightBetErrorMessage;
    const setCashOutAmount =
      side === "left" ? setLeftCashOutAmount : setRightCashOutAmount;
    const setBetButtonShow =
      side === "left" ? setLeftBetButtonShow : setRightBetButtonShow;
    const betArrayRef = side === "left" ? leftBetArrayRef : rightBetArrayRef;
    const setBetArray = side === "left" ? setLeftBetArray : setRightBetArray;

    if (!mainBetId || isCashingOutRef.current) return;

    const rawMultiplier =
      multiplierOverride != null && Number(multiplierOverride) > 0
        ? Number(multiplierOverride)
        : Number(flightValueRef.current);
    if (!(rawMultiplier + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    const incrementor = rawMultiplier.toFixed(2);
    const gameId = betGameIdRef.current || roundIdRef.current || roundId;
    const betId = mainBetId;

    setIsCashingOut(true);
    isCashingOutRef.current = true;
    setShowCashoutButton(false);
    setBetButtonShow(true);
    setCashOutAmount("");

    try {
      const response = await axiosInstance.post("/casino/cash-out", {
        game_id: gameId,
        bet_id: betId,
        gametype: "aviatorx",
        win_multiplier: incrementor
      });

      const result = response.data;

      if (result.isSuccess && result.error === 0) {
        const wonAmount =
          result.data?.cash_out_amount ?? result.cash_out_amount;
        if (wonAmount != null && wonAmount !== "") {
          showWinToast(wonAmount);
        }

        setBetErrorMessage({});

        setShowCashoutButton(false);
        setBetButtonShow(true);
        mainBetIdRef.current = "";
        setMainBetId("");
        setCashOutAmount("");
        betPlacedRef.current = false;

        betArrayRef.current = [];
        setBetArray([]);

        const cashedAmount = parseFloat(betAmount);
        setUserLuckyRecords((prev) =>
          prev.map((bet) => {
            if (String(bet.betId) !== String(mainBetId) || bet.locked) {
              return bet;
            }
            const amount = bet.amountRaw || cashedAmount;
            return {
              ...bet,
              rate: incrementor,
              profit: calcLuckyPlayerProfit(incrementor, amount),
              locked: true
            };
          })
        );
      } else {
        const crashAt = Number(maxFlightRef.current);
        const requested = parseFloat(incrementor);
        const cashedAtOrBelowCrash =
          Number.isFinite(crashAt) &&
          Number.isFinite(requested) &&
          requested <= crashAt + 0.001;
        if (sceneRef.current === "crashed" && !cashedAtOrBelowCrash) {
          isCashingOutRef.current = false;
          showLostToastForUncashedBets();
        }
        const stillFlying = sceneRef.current === "flight";
        setShowCashoutButton(stillFlying);
        if (stillFlying) setBetButtonShow(false);
        setBetErrorMessage({
          status: false,
          message: result.message || "Cashout failed"
        });
      }
    } catch (error) {
      console.error("Cashout error:", error);
      const crashAt = Number(maxFlightRef.current);
      const requested = parseFloat(incrementor);
      const cashedAtOrBelowCrash =
        Number.isFinite(crashAt) &&
        Number.isFinite(requested) &&
        requested <= crashAt + 0.001;
      if (sceneRef.current === "crashed" && !cashedAtOrBelowCrash) {
        isCashingOutRef.current = false;
        showLostToastForUncashedBets();
      }
      const stillFlying = sceneRef.current === "flight";
      setShowCashoutButton(stillFlying);
      if (stillFlying) setBetButtonShow(false);
      setBetErrorMessage({
        status: false,
        message: error.response?.data?.message || "Cashout request failed"
      });
    } finally {
      isCashingOutRef.current = false;
      setIsCashingOut(false);
    }
  };
  cashOutNowRef.current = cashOutNow;

  /** ===== Loading screen timer ===== */
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(t);
  }, []);

  /** ===== Re-sync queued bets on game over (preserve queued bets for next round) ===== */
  useEffect(() => {
    if (scene === "crashed" || scene === "finished") {
      // Do NOT clear betPlacedRef or showCashoutButton here — the server may
      // have already started the next round (start==0 → start==1) while the
      // client crash animation is still playing. Clearing here would undo the
      // bet state set by placeAllBetsNow for the new round.
      // These are properly reset by the start==0 handler and cashOutNow instead.

      // Re-sync betArray state from ref so Cancel button shows for queued bets
      setLeftBetArray([...leftBetArrayRef.current]);
      setRightBetArray([...rightBetArrayRef.current]);
    }
  }, [scene]);

  /** ===== Show cashout button when bet is confirmed and flight is active ===== */
  useEffect(() => {
    if (scene !== "flight") return;
    if (
      !leftIsCashingOutRef.current &&
      leftBetPlacedRef.current &&
      (leftMainBetId || leftMainBetIdRef.current)
    ) {
      setLeftShowCashoutButton(true);
    }
    if (
      !rightIsCashingOutRef.current &&
      rightBetPlacedRef.current &&
      (rightMainBetId || rightMainBetIdRef.current)
    ) {
      setRightShowCashoutButton(true);
    }
  }, [scene, leftMainBetId, rightMainBetId]);

  /** ===== Auto Cashout: trigger cashout when flight value meets threshold ===== */
  useEffect(() => {
    if (scene !== "flight" || flightValue < CASHOUT_MIN_MULTIPLIER) return;
    tryAutoCashoutAtValue(flightValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftIsCashingOut,
    rightIsCashingOut
  ]);

  /** ===== Update cashout amount during flight ===== */
  useEffect(() => {
    if (scene === "flight" && flightValue >= 1) {
      if (leftShowCashoutButton && leftBetAmount > 0) {
        setLeftCashOutAmount(
          (parseFloat(leftBetAmount) * flightValue).toFixed(2)
        );
      }
      if (rightShowCashoutButton && rightBetAmount > 0) {
        setRightCashOutAmount(
          (parseFloat(rightBetAmount) * flightValue).toFixed(2)
        );
      }
    }
  }, [
    flightValue,
    scene,
    leftShowCashoutButton,
    rightShowCashoutButton,
    leftBetAmount,
    rightBetAmount
  ]);

  /** Convenience flags */
  const showTimer = scene === "flight" || scene === "crashed";
  const hasValidPayload = payload != null && !!payload?.evt_id;

  useEffect(() => {
    document.documentElement.classList.add("aviator-bg-black");
    document.documentElement.classList.add("h-100");

    return () => {
      document.documentElement.classList.remove("aviator-bg-black");
      document.documentElement.classList.remove("h-100");
    };
  }, []);

  /** ===== Reusable Bet Panel render function ===== */
  const renderBetPanel = (side) => {
    const isLeft = side === "left";
    const betAmount = isLeft ? leftBetAmount : rightBetAmount;
    const setBetAmount = isLeft ? setLeftBetAmount : setRightBetAmount;
    const betArray = isLeft ? leftBetArray : rightBetArray;
    const showCashoutButton = isLeft
      ? leftShowCashoutButton
      : rightShowCashoutButton;
    const betButtonShow = isLeft ? leftBetButtonShow : rightBetButtonShow;
    const mainBetId = isLeft ? leftMainBetId : rightMainBetId;
    const cashOutAmount = isLeft ? leftCashOutAmount : rightCashOutAmount;
    const isCashingOut = isLeft ? leftIsCashingOut : rightIsCashingOut;
    const betErrorMessageObj = isLeft
      ? leftBetErrorMessage
      : rightBetErrorMessage;
    const autoBet = isLeft ? leftAutoBet : rightAutoBet;
    const autoCashout = isLeft ? leftAutoCashout : rightAutoCashout;
    const autoCashoutAt = isLeft ? leftAutoCashoutAt : rightAutoCashoutAt;
    const setAutoBetState = (val) => {
      if (isLeft) {
        setLeftAutoBet(val);
        leftAutoBetRef.current = val;
      } else {
        setRightAutoBet(val);
        rightAutoBetRef.current = val;
      }
    };
    const setAutoCashoutState = (val) => {
      if (isLeft) {
        setLeftAutoCashout(val);
        leftAutoCashoutRef.current = val;
      } else {
        setRightAutoCashout(val);
        rightAutoCashoutRef.current = val;
      }
    };
    const setAutoCashoutAtState = (val) => {
      if (isLeft) {
        setLeftAutoCashoutAt(val);
        leftAutoCashoutAtRef.current = val;
      } else {
        setRightAutoCashoutAt(val);
        rightAutoCashoutAtRef.current = val;
      }
    };
    const btnColor = "btn-success";

    return (
      <div className="col-6 col-md-6 mb-2 mb-md-4 px-2 py-1 aviator-x-bet-panel w-100">
        <div className="market-runners w-100 d-flex flex-column align-items-stretch">
          {/* Bet amount input */}
          <div className="row mb-2 mx-0 w-100">
            <div className="col-12 px-0">
              <div className="input-group w-100">
                <div className="AVEDI-input-group w-100">
                  <button
                    type="button"
                    onClick={() => setBetAmount((v) => Math.max(1, v - 100))}
                  >
                    <svg
                      width="28"
                      height="40"
                      viewBox="0 0 28 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="AVE-svg-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                        fill="#F7F9FB"
                      />
                    </svg>
                  </button>
                  <input
                    type="text"
                    min="1"
                    max="1000000"
                    className="AVEDI-input-field"
                    value={betAmount}
                    onInput={(e) => {
                      const val = e.target.value
                        .replace(/[^0-9.]/g, "")
                        .replace(/(\..*?)\..*/g, "$1");
                      setBetAmount(
                        val === "" ? "" : Math.max(1, parseInt(val) || 0)
                      );
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setBetAmount((v) => v + 100)}
                  >
                    <svg
                      width="28"
                      height="40"
                      viewBox="0 0 28 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="AVE-svg-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                        fill="#F7F9FB"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Incremental amount buttons */}
          <div className="row mb-2 mx-0 w-100 g-2">
            {[200, 500, 1000, 2000].map((amt) => (
              <div key={amt} className="col-6 col-md-3 px-0">
                <button
                  type="button"
                  className="btn btn-lg btn-outline-light w-100 p-0"
                  onClick={() => setBetAmount(amt)}
                >
                  <span className="amt">{amt}</span>
                </button>
              </div>
            ))}
          </div>

          {/* Bet / Cancel / Cashout buttons */}
          <div className="row mb-1 mb-md-2 mx-0 w-100">
            <div className="col-12 px-0">
              <div role="group" className="btn-group w-100 aviator-bet-actions">
                {(() => {
                  const showCancel = betArray.length > 0 && !showCashoutButton;
                  const showCashout =
                    showCashoutButton && scene === "flight" && !isCashingOut;
                  const showBet = !showCancel && !showCashout;
                  return (
                    <>
                      {/* Bet / Next button — always shown as disabled fallback */}
                      <div
                        style={{
                          width: "100%",
                          display: showBet ? "block" : "none"
                        }}
                      >
                        <button
                          type="button"
                          className={`btn-lg w-100 btn ${btnColor}`}
                          onClick={() => handleBetClick(side)}
                          disabled={!betButtonShow || !hasValidPayload}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50">
                            {scene === "countdown" && hasValidPayload
                              ? "Place"
                              : "Next"}
                          </span>
                        </button>
                      </div>

                      {/* Cancel button */}
                      <div
                        style={{
                          width: "100%",
                          display: showCancel ? "block" : "none"
                        }}
                      >
                        <button
                          type="button"
                          className="btn-lg w-100 btn btn-info"
                          onClick={() => cancelBet(side)}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50">
                            Cancel
                          </span>
                        </button>
                      </div>

                      {/* Cashout button */}
                      <div
                        style={{
                          width: "100%",
                          display: showCashout ? "block" : "none"
                        }}
                      >
                        <input
                          type="hidden"
                          name={`${side}_bet_id`}
                          value={mainBetId}
                        />
                        <button
                          type="button"
                          className="btn-lg w-100 pl-0 pr-0 btn btn-warning"
                          onClick={() => cashOutNow(side)}
                          disabled={isCashingOut}
                        >
                          <span className="shimmer-container">
                            <span className="shimmer"></span>
                          </span>
                          <span className="text-lg font-semibold text-slate-50 mobile-text">
                            Cashout
                          </span>
                          <span className="mobile-text">
                            {cashOutAmount ? ` ${cashOutAmount}` : ""}
                          </span>
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Error/Success message */}
          {betErrorMessageObj.message && (
            <div
              className={`alert ${betErrorMessageObj.status ? "alert-success" : "alert-danger"
                } m-0 p-1`}
              style={{ fontSize: "11px" }}
            >
              {betErrorMessageObj.message}
            </div>
          )}

          {/* Auto Bet / Auto Cashout toggles */}
          <div className="mb-2" style={{ color: "white", paddingTop: "5px" }}>
            <div className="col-12 p-0 d-flex align-items-center">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={autoBet}
                  onChange={(e) => setAutoBetState(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <label>Auto Bet</label>
            </div>
            <div className="col-12 p-0 d-flex align-items-center">
              <label className="switch mr-2">
                <input
                  type="checkbox"
                  checked={autoCashout}
                  onChange={(e) => setAutoCashoutState(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <label>Auto Cashout</label>
            </div>
            <div
              className="w-100"
              style={{
                border: "1px solid white",
                borderRadius: "10px",
                padding: "5px",
                display: autoCashout ? "block" : "none"
              }}
            >
              <div className="form-group mb-0 w-100">
                <label style={{ fontSize: "12px" }}>
                  Cashout at multiplier
                </label>
                <div className="input-group w-100">
                  <div className="AVEDI-input-group w-100">
                    <button
                      type="button"
                      onClick={() =>
                        setAutoCashoutAtState(
                          Math.max(1.1, +(autoCashoutAt - 0.02).toFixed(2))
                        )
                      }
                    >
                      <svg
                        width="28"
                        height="40"
                        viewBox="0 0 28 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="AVE-svg-icon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                          fill="#F7F9FB"
                        />
                      </svg>
                    </button>
                    <input
                      type="number"
                      min="1.10"
                      max="100"
                      step="0.02"
                      className="AVEDI-input-field"
                      value={autoCashoutAt}
                      onChange={(e) =>
                        setAutoCashoutAtState(parseFloat(e.target.value) || 1.1)
                      }
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setAutoCashoutAtState(
                          +(autoCashoutAt + 0.02).toFixed(2)
                        )
                      }
                    >
                      <svg
                        width="28"
                        height="40"
                        viewBox="0 0 28 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="AVE-svg-icon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                          fill="#F7F9FB"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .aviatorx-scope .stage-board {
          position: relative;
          background-color: #000;
          height: 420px;
          border: none;
          border-radius: 8px;
          overflow: hidden;
        }
        .aviatorx-scope .stage-board:before {
          content: "";
          position: absolute;
          opacity: .25;
          top: 0; left: 0; bottom: 0; right: 0;
          background: radial-gradient(ellipse 100% 50% at 50% 50%, rgb(167 89 164 / 69%) 0, rgb(170 95 164 / 81%) 10%, #4d254d 50%, #7d5179 100%);
          transition: filter 0.45s ease, opacity 0.45s ease;
        }
        .aviatorx-scope .stage-board canvas {
          transition: filter 0.45s ease;
        }
        .aviatorx-scope .stage-board.avx-crashed:before {
          filter: grayscale(100%);
          opacity: 0.08;
        }
        .aviatorx-scope .stage-board.avx-crashed canvas {
          filter: grayscale(100%);
        }
        .aviatorx-scope .stage-board.avx-crashed::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background: radial-gradient(
            ellipse 110% 50% at 50% 50%,
            rgba(0, 220, 100, 0.32) 0%,
            rgba(0, 195, 85, 0.2) 28%,
            rgba(0, 165, 70, 0.1) 48%,
            rgba(0, 130, 55, 0.04) 65%,
            transparent 82%
          );
        }
        .loading-game {
          position: absolute;
          inset: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10;
        }
        .waiting-text {
          font-family: 'Roboto Condensed', sans-serif;
          font-size: 20px;
          margin-top: 15px;
          color: white;
          text-shadow: 0 0 10px rgba(0,0,0,0.5);
        }
        .line-loader {
          width: 150px;
          height: 4px;
          background: #333;
          margin-top: 10px;
          border-radius: 2px;
          overflow: hidden;
        }
        .fill-line {
          height: 100%;
          background: #E50539;
          width: 0%;
          animation: fillProgress 5s linear forwards;
        }
        @keyframes fillProgress {
          to { width: 100%; }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating-svg {
          animation: rotate 2s linear infinite;
        }
        .counter-num {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          font-size: 80px;
          font-weight: 700;
          color: #fff;
          z-index: 5;
          text-shadow: 0 0 20px rgba(0,0,0,0.5);
        }
        .aviatorx-scope .counter-num.avx-crashed-display {
          font-family: 'Segoe UI', sans-serif;
          font-weight: 900;
          letter-spacing: 0.025em;
          text-align: center;
          animation: avx-crash-shake 1s ease 1;
        }
        .aviatorx-scope .counter-num.avx-crashed-display .flew-away-text {
          color: rgb(225, 29, 72);
          font-size: 28px;
          font-weight: 900;
          text-transform: uppercase;
          margin-bottom: 8px;
          display: block;
          -webkit-text-stroke: 0.5px #fff;
          text-shadow:
            1px 0 0 #fff, -1px 0 0 #fff,
            0 1px 0 #fff, 0 -1px 0 #fff,
            1.5px 1.5px 0 #fff, -1.5px 1.5px 0 #fff,
            1.5px -1.5px 0 #fff, -1.5px -1.5px 0 #fff;
        }
        .aviatorx-scope .counter-num.avx-crashed-display .avx-crash-multiplier {
          color: rgb(225, 29, 72);
          font-size: clamp(64px, 14vw, 100px);
          line-height: 1;
          -webkit-text-stroke: 0.5px #fff;
          text-shadow:
            1px 0 0 #fff, -1px 0 0 #fff,
            0 1px 0 #fff, 0 -1px 0 #fff,
            1.5px 1.5px 0 #fff, -1.5px 1.5px 0 #fff,
            1.5px -1.5px 0 #fff, -1.5px -1.5px 0 #fff;
        }
        @keyframes avx-crash-shake {
          0% { transform: translate(-50%, -90%); }
          12% { transform: translate(-50%, -50%); }
          24% { transform: translate(-50%, -65%); }
          36% { transform: translate(-50%, -50%); }
          48% { transform: translate(-50%, -57%); }
          60% { transform: translate(-50%, -50%); }
          72% { transform: translate(-50%, -53%); }
          84% { transform: translate(-50%, -50%); }
          100% { transform: translate(-50%, -50%); }
        }
        .flew-away-text {
          color: #E50539;
          font-size: 24px;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 5px;
          display: block;
        }
        .aviatorx-scope .stage-board .Connectedclients {
          position: relative;
          top: -8px;
          left: 16px;
        }
        .aviatorx-scope .stage-board .row > .col-1:last-child > div {
          position: relative !important;
          top: -10px;
          right: -39px;
        }
        .aviatorx-scope .stage-board .AVI-icon__signal-strength {
          transform: scale(0.9);
          transform-origin: center;
        }
        .aviatorx-scope .stage-board label.sound-icon[for="AvxSoundcontrol"] {
          transform: scale(0.76);
          transform-origin: bottom right;
          bottom: -1px !important;
          right: 16px !important;
        }
           @media (max-width:576px){
            .card-body{
              padding: 5px;
            }
            .left-content {
              margin: 2px !important; 
            }
           }

      `}</style>

      <div className="aviatorx-scope aviator-scope aviator-bg-black">
        <ClientLayoutWithOutHeader>
          {/* Loading Screen */}
          {isLoading && (
            <div
              style={{
                background: "#000",
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <img
                src="/assets/images/tpstudio/aviatorstat.webp"
                alt="Loading"
                style={{ animation: "smooth-blink 2s infinite" }}
              />
            </div>
          )}

          <div className="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>

                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                    </div>

                    <div className="card-body">
                      <div className="row aviator-game-row">
                        <div className="col-md-8">
                          <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer h-45">
                            <div className="col-6 col-md-6 titlebarname inline-block Gameidcol mt-2">
                              <p>AviatorX</p>
                            </div>
                            <div
                              className="col-6 col-md-6 inline-block Gameidcol text-right"
                              style={{ marginTop: "4px" }}
                            >
                              <div
                                role="group"
                                aria-label="Second group"
                                className="btn-group btn-group-sm mr-2"
                              >
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  id="Alltab"
                                  className={
                                    activeTabIndex == 1
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(1)}
                                >
                                  Game
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className={
                                    activeTabIndex == 2
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(2)}
                                >
                                  Results
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className="tablink btn btn-primary"
                                  onClick={() => setShowRules(true)}
                                >
                                  Rules
                                </button>
                                <AviatorRules
                                  show={showRules}
                                  onClose={() => setShowRules(false)}
                                />
                              </div>
                            </div>
                          </div>

                          {/* ================= MAIN GAME STAGE ================= */}
                          <div
                            className="game-play"
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div
                              className={`stage-board${
                                scene === "crashed" ? " avx-crashed" : ""
                              }`}
                            >
                              {/* Audio */}
                              <audio ref={bgAudioRef} src={BgSound} loop />
                              <audio
                                ref={flewAwayAudioRef}
                                src={FlewAwaySound}
                              />

                              {/* User count & signal indicators */}
                              <div
                                className="row"
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  padding: "10px",
                                  zIndex: 10
                                }}
                              >
                                <div className="col-1">
                                  <div className="Connectedclients mt-1 ml-1 float-start">
                                    <i
                                      aria-hidden="true"
                                      className="fa fa-user mr-1"
                                      style={{ width: "14px", color: "white" }}
                                    />
                                    <b>
                                      <span id="UsersCount" className="number">
                                        {luckyPlayerCount}
                                      </span>
                                    </b>
                                  </div>
                                </div>
                                <div className="col-10"></div>
                                <div className="col-1">
                                  <div
                                    style={{
                                      float: "inline-end",
                                      position: "relative",
                                      zIndex: 1000
                                    }}
                                  >
                                    <i className="AVI-icon__signal-strength AVI-signal-0">
                                      <span
                                        className="AVI-bar-1"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-2"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-3"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-4"
                                        style={{
                                          backgroundColor: payload
                                            ? "rgb(0, 255, 0)"
                                            : "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                    </i>
                                  </div>
                                </div>
                              </div>

                              {/* Overlay: disconnected */}
                              {!isConnected && (
                                <div
                                  className="loading-game"
                                  style={{
                                    zIndex: 999,
                                    background: "rgba(0,0,0,0.85)"
                                  }}
                                >
                                  <div className="center-loading text-center">
                                    <div
                                      className="waiting-text"
                                      style={{ color: "#ff4444" }}
                                    >
                                      <b>Connection Lost</b>
                                    </div>
                                    <div
                                      className="waiting-text"
                                      style={{
                                        fontSize: "14px",
                                        color: "#ccc"
                                      }}
                                    >
                                      Reconnecting...
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Overlay: waiting / countdown */}
                              {isConnected &&
                                (scene === "countdown" ||
                                  scene === "waiting") && (
                                  <div className="loading-game">
                                    <div className="center-loading text-center">
                                      <svg
                                        className="rotating-svg"
                                        width="80"
                                        height="80"
                                        viewBox="0 0 120 120"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <g fill="#E50539" fillRule="nonzero">
                                          <path d="M67.785 67.77a10.882 10.882 0 0 0 2.995-5.502l18.37-6.36c.47-.163.876-.471 1.16-.88l29.263-42.18a2.343 2.343 0 0 0-.268-2.993L110.153.704a2.344 2.344 0 0 0-3.314 0L95.73 11.813C71.965-5.861 38.683-3.514 17.58 17.588a60.26 60.26 0 0 0-8.829 11.21 2.343 2.343 0 0 0 4.001 2.441 55.575 55.575 0 0 1 8.142-10.336C40.184 1.613 70.512-.68 92.378 15.165l-5.72 5.72c-8.742-5.967-19.302-8.837-29.947-8.1a47.31 47.31 0 0 0-30.183 13.751 47.722 47.722 0 0 0-5.92 7.207 2.344 2.344 0 0 0 3.897 2.605 42.996 42.996 0 0 1 5.337-6.497c14.233-14.234 36.774-16.445 53.436-5.586l-6.818 6.818a33.418 33.418 0 0 0-19.773-4.186A33.338 33.338 0 0 0 36.47 36.48a2.344 2.344 0 0 0 3.314 3.314c8.787-8.786 22.336-10.795 33.215-5.248L58.38 49.163a10.969 10.969 0 0 0-6.164 3.084 10.882 10.882 0 0 0-2.996 5.504l-18.37 6.36c-.47.163-.876.47-1.159.879L.427 107.17a2.343 2.343 0 0 0 .268 2.992l9.152 9.151a2.337 2.337 0 0 0 1.657.687c.6 0 1.2-.23 1.657-.687l11.109-11.109A59.835 59.835 0 0 0 59.99 120a59.873 59.873 0 0 0 42.43-17.571 60.476 60.476 0 0 0 7.162-8.63 2.343 2.343 0 1 0-3.87-2.643 55.793 55.793 0 0 1-6.606 7.959c-19.321 19.32-49.61 21.598-71.487 5.74l5.722-5.723a47.325 47.325 0 0 0 30.058 8.092A47.318 47.318 0 0 0 93.472 93.48a47.82 47.82 0 0 0 5.15-6.09 2.343 2.343 0 0 0-3.82-2.715 43.106 43.106 0 0 1-4.644 5.49c-14.21 14.211-36.783 16.436-53.436 5.587l6.82-6.82a33.416 33.416 0 0 0 19.825 4.182A33.343 33.343 0 0 0 83.53 83.54a2.344 2.344 0 0 0-3.314-3.315c-8.777 8.778-22.34 10.792-33.215 5.25L61.62 70.855a10.97 10.97 0 0 0 6.165-3.084z" />
                                        </g>
                                      </svg>
                                      <div
                                        className="waiting-text"
                                        style={{ fontSize: "1.5em" }}
                                      >
                                        {scene === "waiting"
                                          ? "STARTING NEXT ROUND"
                                          : launchCountdown > 0
                                          ? `STARTING NEXT ROUND IN ${launchCountdown}`
                                          : "STARTING NEXT ROUND"}
                                      </div>
                                      <div className="line-loader">
                                        <div className="fill-line"></div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                              {/* FLIGHT & CRASHED phases: Canvas + multiplier */}
                              {(scene === "flight" || scene === "crashed") && (
                                <>
                                  <canvas
                                    ref={canvasRef}
                                    style={{
                                      position: "absolute",
                                      inset: 0,
                                      zIndex: 1
                                    }}
                                  />

                                  {/* Multiplier Text */}
                                  <div
                                    className={`counter-num${
                                      scene === "crashed"
                                        ? " avx-crashed-display"
                                        : ""
                                    }`}
                                  >
                                    {scene === "crashed" && (
                                      <div className="flew-away-text">
                                        FLEW AWAY!
                                      </div>
                                    )}
                                    <div
                                      className={
                                        scene === "crashed"
                                          ? "avx-crash-multiplier"
                                          : ""
                                      }
                                      style={{
                                        color:
                                          scene === "crashed"
                                            ? undefined
                                            : "#fff"
                                      }}
                                    >
                                      {flightValue >= 1
                                        ? flightValue.toFixed(2) + "x"
                                        : ""}
                                    </div>
                                  </div>

                                  {/* Moving Plane Image (flight + crashed fly-off) */}
                                  {(scene === "flight" ||
                                    scene === "crashed") && (
                                    <img
                                      ref={planeRef}
                                      src="/assets/aviator/images/p.png"
                                      alt="Plane"
                                      style={{
                                        position: "absolute",
                                        width: "80px",
                                        zIndex: 2,
                                        pointerEvents: "none",
                                        transition: "none"
                                      }}
                                    />
                                  )}
                                </>
                              )}

                              {/* Sound toggle */}
                              <label
                                htmlFor="AvxSoundcontrol"
                                className={`fa fa-inverse sound-icon fa-2x ${
                                  soundEnabled
                                    ? "fa-volume-up"
                                    : "fa-volume-off"
                                }`}
                                style={{
                                  position: "absolute",
                                  right: 8,
                                  bottom: 8,
                                  zIndex: 100,
                                  cursor: "pointer"
                                }}
                                title="Toggle sound"
                              >
                                <input
                                  id="AvxSoundcontrol"
                                  type="checkbox"
                                  checked={soundEnabled}
                                  onChange={toggleSound}
                                  style={{ display: "none" }}
                                />
                              </label>
                            </div>
                          </div>

                          {/* Results history & Round ID */}
                          <center
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer">
                              <div className="buttons-div">
                                {lastTenResults.length > 0
                                  ? lastTenResults.map((val, idx) => (
                                      <button
                                        key={idx}
                                        className="video-button border-gradient-btn border-gradient-purple-btn"
                                      >
                                        {val.toFixed(2)}
                                      </button>
                                    ))
                                  : null}
                              </div>
                              <div className="round-div">
                                <div className="label">Round ID:</div>
                                <div className="id">{roundId || "--"}</div>
                              </div>
                            </div>
                          </center>

                          {/* Two-sided betting panels */}
                          <div
                            className="row m-0 p-0 roulette"
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            {renderBetPanel("left")}
                            {renderBetPanel("right")}
                          </div>

                          <div
                            style={{
                              display: activeTabIndex == 2 ? "" : "none"
                            }}
                          >
                            <AvaitorResult resultData={resultData} />
                          </div>
                        </div>
                        <div
                          style={{ height: "5px", backgroundColor: "#000" }}
                        ></div>
                        {/* Right sidebar - Lucky Players */}
                        <div className="col-md-4 aviator-lucky-players-col">
                          <div className="right-content">
                            <div className="card" style={{ border: "0px" }}>
                              <div
                                className="card-header"
                                id="headingTwo"
                                style={{
                                  padding: "0px",
                                  background: "#23292d"
                                }}
                              >
                                <h2
                                  className="mb-0"
                                  style={{
                                    background: "var(--theme-color)",
                                    borderRadius: "0px"
                                  }}
                                >
                                  <button
                                    className="btn btn-link btn-block text-left"
                                    type="button"
                                    style={{
                                      color: "white",
                                      textDecoration: "none"
                                    }}
                                  >
                                    Lucky Players{" "}
                                    <span id="totalLuckyPlayers">
                                      {luckyPlayerCount}
                                    </span>
                                  </button>
                                </h2>
                              </div>
                              <div style={{ backgroundColor: "#2e3438" }}>
                                <div
                                  className="card-body"
                                  style={{ padding: 0, minHeight: "40px" }}
                                >
                                  <div
                                    id="refreshhistory"
                                    className="aviator-lucky-players-scroll"
                                    style={{ paddingLeft: 0, width: "100%" }}
                                  >
                                    <table className="table blastoff-table">
                                      <thead>
                                        <tr>
                                          <th>User</th>
                                          <th>Size</th>
                                          <th>Price</th>
                                          <th>Profit</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {betsList && betsList.length > 0
                                          ? betsList.map((bet) => (
                                              <tr
                                                key={bet.id}
                                                style={{
                                                  backgroundColor: "#eff8ff",
                                                  color: "#000",
                                                  fontSize: "13px",
                                                  textAlign: "center"
                                                }}
                                              >
                                                <td
                                                  style={{
                                                    textAlign: "left",
                                                    fontSize: "13px"
                                                  }}
                                                >
                                                  {bet.uname}
                                                </td>
                                                <td
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  {bet.amount}
                                                </td>
                                                <td
                                                  style={{ fontSize: "13px" }}
                                                >
                                                  {bet.rate}
                                                </td>
                                                <td
                                                  style={{
                                                    textAlign: "center",
                                                    fontSize: "13px"
                                                  }}
                                                >
                                                  {bet.profit}
                                                </td>
                                              </tr>
                                            ))
                                          : null}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>{" "}
                        {/*  ends here  */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={false}
        closeButton={false}
        newestOnTop
        limit={3}
      />
      <style>
        {`
          .av-win-toast,
          .av-bet-toast,
          .av-lost-toast {
            border-radius: 8px !important;
            padding: 12px 20px !important;
            min-height: unset !important;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.38) !important;
            overflow: hidden;
          }
          .av-win-toast {
            background: #2d7a3a !important;
          }
          .av-bet-toast {
            background: #43a047 !important;
          }
          .av-lost-toast {
            background: #c62828 !important;
          }
          .av-win-toast .Toastify__toast-body,
          .av-bet-toast .Toastify__toast-body,
          .av-lost-toast .Toastify__toast-body {
            padding: 0 !important;
            margin: 0 !important;
          }
          .av-win-toast .Toastify__progress-bar,
          .av-bet-toast .Toastify__progress-bar,
          .av-lost-toast .Toastify__progress-bar {
            background: rgba(0, 0, 0, 0.2) !important;
            height: 4px;
          }
          .av-toast-body {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #ffffff;
            font-size: 18px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
          }
          .av-toast-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }
          .av-toast-icon svg {
            display: block;
            width: 24px;
            height: 24px;
          }
          .av-toast-text {
            padding-top: 1px;
          }
        `}
      </style>
    </>
  );
}
